import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/http.api';
import TranscriptEditor from '../../components/TranscriptEditor';
import { LoginForm } from '../login/LoginForm';
import { useAuth } from '../../hooks/useAuth';
import { groupBlocksBySpeaker } from '../../components/TranscriptEditor/utils/transcriptOperations';
import TranscriptService from '../../services/transcript/TranscriptService';

/**
 * TranscriptEditor Page
 * 
 * Advanced transcript editing interface for sessions
 */
const TranscriptEditorPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, error: authError, getAuth } = useAuth();

  const [sessionData, setSessionData] = useState(null);
  const [transcriptBlocks, setTranscriptBlocks] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptFetchCompleted, setTranscriptFetchCompleted] = useState(false);
  const [error, setError] = useState(null);

  const transcriptStatus = sessionData?.transcriptStatus || sessionData?.transcript_status;

  /**
   * Phase 1: Fetch session data only (fast - 1 API call)
   * Video can render immediately after this
   */
  useEffect(() => {
    if (!user || !sessionId) return;

    const fetchSession = async () => {
      try {
        setSessionLoading(true);
        setError(null);

        const sessionRes = await apiClient.get(`/api/reports/sessions/${sessionId}`);
        const session = sessionRes.data;
        setSessionData(session);

        const status = session.transcriptStatus || session.transcript_status;
        if (status !== 'COMPLETED') {
          const statusMessages = {
            'PENDING': 'Transcript processing has not started yet.',
            'PROCESSING': 'Transcript is still being processed. Please wait until processing is complete.',
            'FAILED': 'Transcript processing failed. Please retry processing.',
          };
          const message = statusMessages[status] || 'Transcript is not available for editing yet.';
          setError(`${message}\n\nTranscript editing is only available when processing is fully completed.`);
        }
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setSessionLoading(false);
      }
    };

    fetchSession();
  }, [user, sessionId]);

  /**
   * Phase 2: Fetch transcript in background (when session ready and transcript completed)
   * Video already visible and loading
   */
  useEffect(() => {
    if (!sessionData || transcriptStatus !== 'COMPLETED') return;

    const fetchTranscript = async () => {
      try {
        setTranscriptLoading(true);
        setTranscriptFetchCompleted(false);

        let transcriptSegments = [];
        let usedPagedEndpoint = false;

        try {
          const firstPage = await TranscriptService.getTranscriptSegments(sessionId, 0, 1000);
          if (firstPage.success && Array.isArray(firstPage.data?.segments)) {
            usedPagedEndpoint = true;
            transcriptSegments = firstPage.data.segments;

            let offset = firstPage.data.offset + firstPage.data.limit;
            const total = firstPage.data.total;
            const hardCap = 20000;

            while (offset < total && transcriptSegments.length < hardCap) {
              const page = await TranscriptService.getTranscriptSegments(sessionId, offset, 1000);
              if (!page.success || !Array.isArray(page.data?.segments) || page.data.segments.length === 0) {
                break;
              }
              transcriptSegments = transcriptSegments.concat(page.data.segments);
              offset += page.data.limit;
            }
          }
        } catch (e) {
          usedPagedEndpoint = false;
        }

        const transcriptRes = usedPagedEndpoint
          ? { data: { segments: transcriptSegments } }
          : await apiClient.get(`/api/reports/transcripts/${sessionId}`);

        const blocks = (transcriptRes.data.segments || []).map((segment, index) => ({
          id: segment.id || `segment-${index}`,
          text: segment.text || '',
          speaker: segment.speaker || 'UNKNOWN',
          start: segment.start || 0,
          end: segment.end || 0,
          words: segment.words || [],
          paragraphNumber: segment.paragraph_number || index,
          metadata: {
            isEdited: segment.is_edited || false,
            lastModified: segment.last_modified || new Date().toISOString(),
            version: segment.version || 1,
          },
        }));

        const groupedBlocks = groupBlocksBySpeaker(blocks);
        setTranscriptBlocks(groupedBlocks);
      } catch (transcriptError) {
        console.log('No transcript found yet:', transcriptError);
        setTranscriptBlocks([]);
      } finally {
        setTranscriptLoading(false);
        setTranscriptFetchCompleted(true);
      }
    };

    fetchTranscript();
  }, [sessionData, transcriptStatus, sessionId]);


  /**
   * Handle export
   */
  const handleExport = async (format, blocks, title) => {
    try {
      // Import export utilities
      const { exportTranscript } = await import('../../components/TranscriptEditor/utils/exportFormats');
      
      // Use current blocks from editor (not from backend)
      // This ensures we export the latest edited version
      // Use session title if available, otherwise use default
      const exportTitle = title || sessionData?.title || 'Transcript';
      exportTranscript(blocks, format, exportTitle);
    } catch (err) {
      console.error('Export failed:', err);
      alert(`Failed to export transcript: ${err.message || 'Unknown error'}`);
    }
  };

  // Show login form if not authenticated
  if (!user) {
    return (
      <LoginForm
        onSSOLogin={getAuth}
        ssoLoading={authLoading}
        ssoError={authError}
      />
    );
  }

  // Show loading state (session only - video renders after this)
  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/sessions')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  // Show no transcript state (only after transcript fetch completed and we got 0 blocks)
  if (transcriptFetchCompleted && (!transcriptBlocks || transcriptBlocks.length === 0)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Transcript</h1>
          <p className="text-gray-600 mb-4">
            This session doesn't have a transcript yet. Please generate one first.
          </p>
          <button
            onClick={() => navigate(`/sessions/${sessionId}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Session Details
          </button>
        </div>
      </div>
    );
  }

  // Render TranscriptEditor immediately when session ready and transcript is COMPLETED
  // Video loads right away; transcript area shows loading until transcript arrives
  // Get video URL from session data
  const videoUrl = sessionData?.videoUrl || sessionData?.video_url || sessionData?.file_path;
  
  // If videoUrl is relative, construct full URL
  let fullVideoUrl = videoUrl && !videoUrl.startsWith('http') 
    ? `${process.env.REACT_APP_API_BASE_URL}${videoUrl}` 
    : videoUrl;
  
  // Add token as query parameter for streaming authentication
  // This allows HTML5 video element to stream directly using Range requests
  if (fullVideoUrl && fullVideoUrl.includes('/api/')) {
    const token = localStorage.getItem('diploAccessKey');
    if (token) {
      const separator = fullVideoUrl.includes('?') ? '&' : '?';
      fullVideoUrl = `${fullVideoUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Transcript Editor */}
      <div className="flex-1 overflow-hidden">
        <TranscriptEditor
          sessionId={sessionId}
          videoUrl={fullVideoUrl}
          initialBlocks={transcriptBlocks}
          transcriptLoading={transcriptLoading}
          onExport={handleExport}
          sessionTitle={sessionData?.title}
          backUrl={`/sessions/${sessionId}`}
        />
      </div>
    </div>
  );
};

export default TranscriptEditorPage;


